import { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 25000;

/**
 * Polls /api/chancellor/heartbeat every ~25s and returns aggregated live state.
 * Public endpoint, no auth required. One shared shape across the whole app:
 *
 *   { ready, awakeSince, awakeSeconds, soulsWatched, soulsJudgedTotal,
 *     oathsSworn, events24h, latestEvent, awakeLabel }
 *
 * `awakeLabel` is computed client-side and ticks every second so the
 * "presiding · awake 3h 17m" pill feels live without an extra fetch.
 */
export function useChancellorHeartbeat({ updateTabTitle = false } = {}) {
  const [hb, setHb] = useState(null);
  const [tick, setTick] = useState(0);
  const lastSeenSinceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await fetch(`${API_URL}/api/chancellor/heartbeat`, { credentials: 'omit' });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        // If the server restarted, awake_since changes — reset our local clock.
        if (d?.awake_since && d.awake_since !== lastSeenSinceRef.current) {
          lastSeenSinceRef.current = d.awake_since;
        }
        setHb(d);
      } catch { /* silent */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, POLL_INTERVAL_MS);
    // Background tabs throttle setInterval — refresh immediately on return.
    const onVisible = () => { if (!document.hidden) fetchOnce(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', fetchOnce);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', fetchOnce);
    };
  }, []);

  // 1s ticker so the "awake Xh Ym" label updates without re-polling.
  useEffect(() => {
    if (!hb) return undefined;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [hb]);

  // Compute live awake duration from awake_since + drift since last poll
  const awakeSeconds = hb
    ? Math.max(0, Math.floor((Date.now() - new Date(hb.awake_since).getTime()) / 1000))
    : 0;
  const awakeLabel = formatAwakeLabel(awakeSeconds);

  // Document title — "(👁 47 souls · GFG)"
  useEffect(() => {
    if (!updateTabTitle || !hb) return undefined;
    const original = document.title;
    const watching = hb.souls_watched ?? 0;
    document.title = watching > 0
      ? `(${watching}) † The Chancellor · GFG`
      : '† The Chancellor · GFG';
    return () => { document.title = original; };
  }, [updateTabTitle, hb]);

  return {
    ready: !!hb,
    awakeSince: hb?.awake_since,
    awakeSeconds,
    awakeLabel,
    soulsWatched: hb?.souls_watched ?? 0,
    soulsJudgedTotal: hb?.souls_judged_total ?? 0,
    oathsSworn: hb?.oaths_sworn ?? 0,
    events24h: hb?.events_24h ?? 0,
    latestEvent: hb?.latest_event ?? null,
    // Re-render trigger so consumers get fresh awakeLabel even between polls
    _tick: tick,
  };
}

function formatAwakeLabel(secs) {
  if (!secs || secs < 60) return `${secs || 0}s`;
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
